import React from 'react';
import LegalLayout from '../../components/LegalLayout';

const Privacy = () => (
  <LegalLayout title="MyBusinessAdPro Privacy Policy" lastUpdated="February 2026 · Live at /legal/privacy">
    <h2>1. Introduction</h2>
    <p>MyBusinessAdPro ("<strong>MBAP</strong>", "<strong>we</strong>", "<strong>our</strong>", or "<strong>us</strong>") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you use our AI-powered small-business advertising platform.</p>

    <h2>2. Information We Collect</h2>
    <p>We collect information that you provide directly to us, including:</p>
    <ul>
      <li>Account information (name, email, business name)</li>
      <li>Business information (business type, website, contact details)</li>
      <li>Payment information (processed securely via Stripe — we never store your PAN)</li>
      <li>Content you create (ad briefs, generated headlines, body copy, images, saved campaigns)</li>
      <li>Review text you paste for AI response generation</li>
      <li>Communications with us</li>
      <li>Mobile number + explicit consent record, when you opt in to WhatsApp updates via the on-site popup</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <ul>
      <li>Provide, maintain and improve our services</li>
      <li>Process transactions and send related information</li>
      <li>Generate AI advertising content by routing your prompts to our approved large-language model providers</li>
      <li>Enable posting to Meta (Facebook / Instagram) and other social platforms where you have connected them</li>
      <li>Send technical notices, updates and support messages</li>
      <li>Send marketing messages only where you have given explicit prior consent</li>
      <li>Respond to your comments, questions and requests</li>
    </ul>

    <h2>4. Sharing of Information</h2>
    <p>We may share your information with:</p>
    <ul>
      <li><strong>Meta Platforms</strong> (Facebook / Instagram) where you post or advertise through those channels</li>
      <li><strong>Google</strong> (Business Profile, Ads) for listing and campaign management</li>
      <li><strong>Stripe</strong> for payment processing</li>
      <li><strong>Google Gemini / Emergent Integrations</strong> for AI text and image generation (server-side prompt delivery)</li>
      <li><strong>MongoDB Atlas</strong> for encrypted database storage</li>
      <li><strong>Twilio / Meta WhatsApp Cloud API</strong> for outbound WhatsApp messaging (Phase 2 — not yet enabled)</li>
      <li>Service providers who assist in our operations under appropriate data-processing agreements</li>
      <li>As required by law or to protect rights and safety</li>
    </ul>
    <p>We <strong>do not sell your personal data</strong> and do not allow any partner to use your data for their own marketing.</p>

    <h2>5. Data Security</h2>
    <ul>
      <li>All data encrypted in transit (TLS 1.2+)</li>
      <li>Passwords hashed with bcrypt</li>
      <li>Card data tokenised by Stripe — we never see raw card numbers</li>
      <li>Role-based access control and admin audit logging</li>
      <li>Daily automated GDPR cleanup job (03:00 UTC)</li>
    </ul>

    <h2>6. Your Rights</h2>
    <p>Under UK GDPR and the Data Protection Act 2018, you have the right to:</p>
    <ul>
      <li>Access your personal information</li>
      <li>Correct inaccurate data</li>
      <li>Request deletion of your data ("right to erasure")</li>
      <li>Object to processing of your data</li>
      <li>Data portability</li>
      <li>Withdraw consent at any time without affecting prior processing</li>
      <li>Lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
    </ul>
    <p>To exercise any of these rights, email <a href="mailto:contact@mybusinessadpro.co.uk">contact@mybusinessadpro.co.uk</a>. We will respond within 30 calendar days.</p>

    <h2>7. Cookies and Tracking</h2>
    <p>We use cookies and similar tracking technologies to track activity on our platform and hold certain information. See the separate <a href="/legal/cookies">Cookie Policy</a> for full detail. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</p>

    <h2>8. Third-Party Services</h2>
    <p>Our platform integrates with third-party services including Meta (Facebook / Instagram), Google, Stripe, and Google Gemini. These services have their own privacy policies governing the use of your information.</p>

    <h2>9. Data Retention</h2>
    <ul>
      <li>Active account data: retained while the account is active</li>
      <li>Cancelled accounts: 30 days from cancellation, then permanently deleted</li>
      <li>WhatsApp subscriber records: retained while subscribed; personal data anonymised 12 months after the unsubscribed_at date</li>
      <li>Billing records: retained for 7 years for HMRC compliance</li>
      <li>Audit logs: retained for 2 years</li>
    </ul>

    <h2>10. Changes to This Policy</h2>
    <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>

    <h2>11. Contact Us</h2>
    <p>If you have any questions about this Privacy Policy, please contact us at:</p>
    <p>
      <strong>Email:</strong> <a href="mailto:contact@mybusinessadpro.co.uk">contact@mybusinessadpro.co.uk</a><br />
      <strong>Data Controller:</strong> Krinal Ahir Ltd, trading as MyBusinessAdPro<br />
      <strong>Registered Office:</strong> The Spinney, 12 Scothern Lane, Sudbrooke LN2 2QT<br />
      <strong>Company No.:</strong> 11940942
    </p>
  </LegalLayout>
);
export default Privacy;
